/**
 * Registry for the single in-flight Ask Gemini answer stream.
 * @remarks
 * Ask Gemini streams exactly one answer at a time, but the code that has to
 * ABORT a stream is not always the code that owns the `EventSource` ref.
 * `createNewThread` (the sidebar's "New Chat" button, and the `?q=` auto-send
 * path) must kill whatever is still streaming, or the abandoned stream keeps
 * appending its remaining tokens into the freshly-cleared conversation — the
 * "second prompt replays the first prompt's answer" symptom of SE-6900.
 *
 * This replaces `shouldResetConnectionVar`, a reactive flag that was written by
 * both `createNewThread` and `useConnectionOperations` and read by nothing, so
 * the teardown it was named for never actually happened.
 *
 * The handle lives in `activeStreamVar` — the store slot the dead flag used to
 * occupy — because a reactive variable is this project's sanctioned holder for
 * mutable chat state. A single live handle is safe: the chat screen mounts one
 * `useSSE` instance, and the registry is cleared on unmount.
 */

import { activeStreamVar, type AbortableStream } from "@/stores/knowledgeGraph";

export type { AbortableStream };

/**
 * Records the stream that is currently in flight.
 * @param stream - The live stream, or null to clear the registry
 */
export const setActiveStream = (stream: AbortableStream | null): void => {
  activeStreamVar(stream);
};

/**
 * Clears the registry only if it still points at the given stream.
 * @remarks Prevents a stream that has already been superseded from clearing a
 * newer one when its own teardown runs late.
 * @param stream - The stream believed to be active
 */
export const clearActiveStream = (stream: AbortableStream): void => {
  if (activeStreamVar() === stream) {
    activeStreamVar(null);
  }
};

/**
 * Closes whatever stream is in flight, if any.
 * @returns True when a stream was actually torn down
 */
export const closeActiveStream = (): boolean => {
  const stream = activeStreamVar();
  if (!stream) {
    return false;
  }
  activeStreamVar(null);
  stream.close();
  return true;
};
